import Card from "@material-ui/core/Card";
import makeStyles from "@material-ui/core/styles/makeStyles";
import AppHeader from "@saleor/components/AppHeader";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import SearchInput from "@saleor/components/SearchBar/SearchInput";
import { ShopInfo_shop_languages } from "@saleor/components/Shop/types/ShopInfo";
import FilterTabs, { FilterTab } from "@saleor/components/TableFilter";
import { maybe } from "@saleor/misc";
import { SearchPageProps } from "@saleor/types";
import React from "react";
import { IntlShape, useIntl } from "react-intl";

import { TranslatableEntities } from "../../urls";

export interface TranslationsEntitiesListPageProps extends SearchPageProps {
  children: React.ReactNode;
  filters: TranslationsEntitiesFilters;
  language: ShopInfo_shop_languages;
  onBack: () => void;
}

export interface TranslationsEntitiesFilters {
  current: TranslationsEntitiesListFilterTab;
  onShopTabClick: () => void;
  onCategoriesTabClick: () => void;
  onCollectionsTabClick: () => void;
  onProductsTabClick: () => void;
  onProductVariantTabClick: () => void;
  onProductTagTabClick: () => void;
  onMenuItemTabClick: () => void;
  onMenuBannerTabClick: () => void;
  onExtraChargesTabClick: () => void;
  onSalesTabClick: () => void;
  onVouchersTabClick: () => void;
  onShippingMethodTabClick: () => void;
  onPagesTabClick: () => void;
  onPageHeaderTabClick: () => void;
  onProductTypesTabClick: () => void;
  onHotWordTabClick: () => void;
  onCornerMarkTabClick: () => void;
  onComboTabClick: () => void;
  onLoginPopupClick: () => void;
  onProductActivationSettingClick: () => void;
  onProductLensTypeClick: () => void;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      display: "flex",
      flexWrap: "wrap",
      padding: theme.spacing(1, 3)
    },
    tabActions: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 3, 2),
      textAlign: "right"
    }
  }),
  {
    name: "FilterActions"
  }
);

export type TranslationsEntitiesListFilterTab = keyof typeof TranslatableEntities;

function getSearchPlaceholder(
  tab: TranslationsEntitiesListFilterTab,
  intl: IntlShape
): string {
  switch (tab) {
    case "shop":
      return intl.formatMessage({
        defaultMessage: "Search Shop"
      });

    case "categories":
      return intl.formatMessage({
        defaultMessage: "Search Category"
      });

    case "collections":
      return intl.formatMessage({
        defaultMessage: "Search Collection"
      });

    case "products":
      return intl.formatMessage({
        defaultMessage: "Search Product"
      });
    case "productVariant":
      return intl.formatMessage({
        defaultMessage: "Search Product Variant"
      });
    case "productTag":
      return intl.formatMessage({
        defaultMessage: "Search Product Tag"
      });
    case "extraCharges":
      return intl.formatMessage({
        defaultMessage: "Search ExtraCharges"
      });
    case "menuItem":
      return intl.formatMessage({
        defaultMessage: "Search MenuItem"
      });
    case "menuBanner":
      return intl.formatMessage({
        defaultMessage: "Search MenuBanner"
      });
    case "sales":
      return intl.formatMessage({
        defaultMessage: "Search Sale"
      });

    case "vouchers":
      return intl.formatMessage({
        defaultMessage: "Search Voucher"
      });
    case "shippingMethod":
      return intl.formatMessage({
        defaultMessage: "Search Shipping Method"
      });
    case "pages":
      return intl.formatMessage({
        defaultMessage: "Search Page"
      });

    case "pageHeader":
      return intl.formatMessage({
        defaultMessage: "Search Page Header"
      });

    case "productTypes":
      return intl.formatMessage({
        defaultMessage: "Search Product Type"
      });

    case "hotWord":
      return intl.formatMessage({
        defaultMessage: "Search Hot Word"
      });
    case "cornerMark":
      return intl.formatMessage({
        defaultMessage: "Search Corner Mark"
      });
    case "combo":
      return intl.formatMessage({
        defaultMessage: "Search Combo"
      });
    case "loginPopup":
      return intl.formatMessage({
        defaultMessage: "Search LoginPopup"
      });

    case "productActivationSetting":
      return intl.formatMessage({
        defaultMessage: "Search productActivationSetting"
      });
    case "productLensType":
      return intl.formatMessage({
        defaultMessage: "Search productLensType"
      });
    default:
      return "...";
  }
}

const tabs: TranslationsEntitiesListFilterTab[] = [
  "shop",
  "categories",
  "collections",
  "products",
  "productVariant",
  "productTag",
  "extraCharges",
  "menuItem",
  "menuBanner",
  "sales",
  "vouchers",
  "shippingMethod",
  "pages",
  "pageHeader",
  "productTypes",
  "hotWord",
  "cornerMark",
  "combo",
  "loginPopup",
  "productActivationSetting",
  "productLensType"
];

const TranslationsEntitiesListPage: React.FC<TranslationsEntitiesListPageProps> = props => {
  const { filters, language, onBack, children, ...searchProps } = props;

  const classes = useStyles(props);
  const intl = useIntl();
  const currentTab = tabs.indexOf(filters.current);

  return (
    <Container>
      <AppHeader onBack={onBack}>
        {intl.formatMessage({
          defaultMessage: "Languages"
        })}
      </AppHeader>
      <PageHeader
        title={intl.formatMessage(
          {
            defaultMessage: "Translations to {language}",
            description: "header"
          },
          {
            language: maybe(() => language.language, "...")
          }
        )}
      />
      <Card>
        <FilterTabs currentTab={currentTab}>
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Shop"
            })}
            key={0}
            onClick={filters.onShopTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Categories"
            })}
            key={1}
            onClick={filters.onCategoriesTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Recommendation Groups"
            })}
            key={2}
            onClick={filters.onCollectionsTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Products"
            })}
            key={3}
            onClick={filters.onProductsTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Product Variant"
            })}
            key={4}
            onClick={filters.onProductVariantTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Product Tag"
            })}
            key={5}
            onClick={filters.onProductTagTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Extra Charges"
            })}
            key={6}
            onClick={filters.onExtraChargesTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Menu Item"
            })}
            key={7}
            onClick={filters.onMenuItemTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Menu Banner"
            })}
            key={8}
            onClick={filters.onMenuBannerTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Sales"
            })}
            key={9}
            onClick={filters.onSalesTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Vouchers"
            })}
            key={10}
            onClick={filters.onVouchersTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Shipping Method"
            })}
            key={11}
            onClick={filters.onShippingMethodTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Pages"
            })}
            key={12}
            onClick={filters.onPagesTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "PageHeader"
            })}
            key={13}
            onClick={filters.onPageHeaderTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Product Types"
            })}
            key={14}
            onClick={filters.onProductTypesTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Hot Word"
            })}
            key={15}
            onClick={filters.onHotWordTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Corner Mark"
            })}
            key={16}
            onClick={filters.onCornerMarkTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Product Detail ProductSet"
            })}
            key={17}
            onClick={filters.onComboTabClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Login Popup"
            })}
            key={18}
            onClick={filters.onLoginPopupClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Product Activation Setting"
            })}
            key={19}
            onClick={filters.onProductActivationSettingClick}
          />
          <FilterTab
            label={intl.formatMessage({
              defaultMessage: "Product Lens Type"
            })}
            key={19}
            onClick={filters.onProductLensTypeClick}
          />
        </FilterTabs>

        <div className={classes.root}>
          <SearchInput
            placeholder={getSearchPlaceholder(filters.current, intl)}
            {...searchProps}
          />
        </div>
        {children}
      </Card>
    </Container>
  );
};
TranslationsEntitiesListPage.displayName = "TranslationsEntitiesListPage";
export default TranslationsEntitiesListPage;
